import { Box, keyframes, type BoxProps } from "@chakra-ui/react";
import { useEffect, useRef, useState, type ReactNode } from "react";

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

const STAGGER_STEP_S = 0.07;

const MAX_STAGGER_STEPS = 4;

const rise = keyframes({
  from: { opacity: 0, transform: "translateY(18px)" },
  to: { opacity: 1, transform: "none" },
});

type RevealProps = BoxProps & {
  children: ReactNode;
  index?: number;
};

// Content that rises into view. The page's own HTML plays the rise as it first paints, so whatever is
// on screen shows without waiting for the script; once the script runs, what is still below the fold
// is put back out of sight and rises when it is scrolled to.
export const Reveal = ({ children, index = 0, ...rest }: RevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"painted" | "waiting" | "shown">("painted");
  const animation = `${rise} 0.7s ${EASE} ${Math.min(index, MAX_STAGGER_STEPS) * STAGGER_STEP_S}s both`;

  useEffect(() => {
    const element = ref.current;
    if (!element || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (element.getBoundingClientRect().top < window.innerHeight * 0.92) return;
    setState("waiting");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        setState("shown");
      },
      { threshold: 0.08, rootMargin: "0px 0px -8% 0px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      className="reveal"
      data-reveal={state}
      {...rest}
      sx={{
        animation,
        "&[data-reveal=waiting]": { animation: "none", opacity: 0, transform: "translateY(18px)" },
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      {children}
    </Box>
  );
};
